import React, { useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty'
import { selectCloudReportFilters, selectProductMix } from './reports.selectors'
import { useDispatch, useSelector } from 'react-redux'
import BusyIndicator, { REGIONS } from '../../widgets/busyIndicator'
import { LinkContainer } from 'react-router-bootstrap'
import { setCloudReportFilters } from './reports.slice'
import { FilterOperators } from './reports.constants'
import DashboardCard from '../../components/cards/DashboardCard'
import { ChartContainer, ChartTooltip } from '../../components/ui/chart'
import { Label, Pie, PieChart, Cell } from 'recharts'

const ProductMixChart = () => {
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const productMix = useSelector(selectProductMix)
	const chartRef = useRef()
	const productMixData = Object.values(!isEmpty(productMix) ? productMix : [])

	// Map tier name to hex color based on css classes defined in app.css
	const getTierFill = (name) => {
		const key = name && name.trim() !== '' ? name : 'Unknown'
		switch (key) {
			case 'Unknown': return '#ff6e6e'
			case '-None-': return '#c9daff'
			case 'Starter': return '#afc8ff'
			case 'Basic': return '#96b7ff'
			case 'Foundation': return '#76a1ff'
			case 'Essential': return '#76a1ff'
			case 'Growth': return '#6695fb'
			case 'Classic': return '#598dff'
			case 'Standard': return '#447eff'
			case 'Platinum': return '#3775ff'
			case 'Premium': return '#1e64ff'
			case 'Custom': return '#3600ff'
			case 'FastBound': return '#000000'
			case 'NSSF Member Starter': return '#97ffac'
			case 'NSSF Member Essential': return '#83ff9c'
			case 'NSSF Member Growth': return '#5bff7c'
			case 'NSSF Member Standard': return '#36ff5e'
			case 'NSSF Member Platinum': return '#26e94d'
			case 'NSSF Member Premium': return '#0ed135'
			case 'NSSF Member Custom': return '#009c1f'
			default: return '#ff6e6e'
		}
	}

	const productMixDataWithFill = productMixData.map((p) => ({ ...p, fill: getTierFill(p?.tierName) }))
	const label = productMixDataWithFill.map((pmix) =>
		pmix?.tierName !== '' ? pmix?.tierName : 'Unknown'
	)

	
	const selectedSalesReportFilters = useSelector(selectCloudReportFilters)
	const productMixAmounts = productMixDataWithFill.map((pmix) => pmix?.amount)

	const numberWithCommas = (number) => {
		return number.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
	}

	const getFormattedLabels = (phrase) => {
		return phrase
			.toLowerCase()
			.split(' ')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ')
	}

	const customLabels = label.map(
		(label, index) =>
			productMixAmounts.length > 0 &&
			`${getFormattedLabels(label)} $${numberWithCommas(
				productMixAmounts[index]
			)}`
	)
	const sumOfValues =
		productMixAmounts.length > 0 &&
		productMixAmounts.reduce(function (a, b) {
			return a + b
		}, 0)
	const formattedSumOfValues = sumOfValues && sumOfValues.toFixed(2)
	const data = {
		labels: customLabels,
		datasets: [
			{
				backgroundColor: [
					'#ff6e6e',
					'#c9daff',
					'#afc8ff',
					'#96b7ff',
					'#76a1ff',
					'#76a1ff',
					'#6695fb',
					'#598dff',
					'#447eff',
					'#3775ff',
					'#1e64ff',
					'#3600ff',
					'#000000',
					'#97ffac',
					'#83ff9c',
					'#5bff7c',
					'#36ff5e',
					'#26e94d',
					'#0ed135',
					'#009c1f',
				],
				data: productMixAmounts,
				dataObj: productMixDataWithFill,
				borderWidth: 0,
			},
		],
	}
	

	const toKey = (name) =>
		(name && name !== '' ? name : 'Unknown')
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/(^-|-$)/g, '')

	const chartConfig = productMixDataWithFill.reduce((acc, pMix) => {
		const display = pMix.tierName && pMix.tierName !== '' ? pMix.tierName : 'Unknown'
		const key = toKey(display)
		acc[key] = { label: display, color: pMix.fill }
		return acc
	}, {})


	return (
		<DashboardCard title='Product Mix'>
			<BusyIndicator region={REGIONS.FETCH_PRODUCT_MIX}>
					<div className='grid grid-cols-2 gap-2'>
						<div className='md:col-span-1 xl:col-span-1 sm:col-span-1'>
							{!isEmpty(data) && (
								<ChartContainer className='w-full h-full' config={chartConfig}>

									<PieChart>
										<ChartTooltip cursor={false} />
							<Pie data={productMixDataWithFill}
											innerRadius={80}
											strokeWidth={5}
											dataKey="amount"
											nameKey="tierName"
										>
								{productMixDataWithFill.map((pMix, idx) => (
									<Cell className='bg-primary' key={pMix.id || idx} fill={pMix.fill} />
											))}
											<Label
               content={({ viewBox }) => {
                 if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                   return (
                     <text
                       x={viewBox.cx}
                       y={viewBox.cy}
                       textAnchor="middle"
                       dominantBaseline="middle"
                     >
                       <tspan
                         x={viewBox.cx}
                         y={viewBox.cy}
                         className="fill-foreground text-xl font-bold"
                       >
                         {`$${numberWithCommas(formattedSumOfValues)}`}
                       </tspan>
												<tspan
													x={viewBox.cx}
													y={(viewBox.cy || 0) + 24}
													className="fill-muted-foreground"
												>
													Total Sales
												</tspan>
                     </text>
                   )
                 }
               }}
             />
										</Pie>
									</PieChart>
								</ChartContainer>
							)}
						</div>
						<div className='grid grid-cols-2 gap-2'>
								{productMixData.map((pMix) => {
									return (
										<LinkContainer
											to={'/SalesReport'}
											onClick={() => {
												dispatch(
													setCloudReportFilters({
														...selectedSalesReportFilters,
														cloudTierId: {
															filterValue: pMix.id,
															filterOperationType:
																pMix.id < 0
																	? pMix.IsEmpty
																	: FilterOperators.Equal,
														},
														cloudTier: isEmpty(pMix.tierName)
															? 'Unknown'
															: pMix.tierName,
													})
												)
											}}
											key={pMix.id}
										>

											<div key={pMix.id} className='flex flex-row items-center'>
												<div
													className={`dot mr-2 rounded-full bg-${
														pMix.tierName !== ''
															? pMix.tierName.replaceAll(' ', '-')
															: 'Unknown'
													}`}
												></div>
												<div className='flex flex-col'>
													<span className='text-xs'>
														{pMix.tierName !== ''
															? pMix.tierName
															: 'Unknown'}
													</span>
													<span className='text-xs font-normal'>
														{`$${numberWithCommas(pMix.amount)}`}
													</span>
												</div>
											</div>
										
									</LinkContainer>
								)
								})}
							</div>
						</div>
			</BusyIndicator>
		</DashboardCard>
	)
}

export default ProductMixChart
